import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2, Wifi, WifiOff } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { EmptyState, LoadingState, ErrorState } from '@/components/ui/states';
import { ScopeLock } from '@/components/ui/scope-lock';
import { apiMessage, pruneParams, useDebounced, useRequireScope } from '../helpers';
import type { Branch } from '../types';
import BranchFormDialog from './BranchFormDialog';
import ConfirmDialog from './ConfirmDialog';

interface Props {
  /**
   * Raised after a create, an edit or a delete lands. The Settings tab needs
   * nothing — this panel re-reads its own list — but the setup wizard embeds it
   * as a step, and the step's "done" tick is read back from the API rather than
   * assumed from the click, so it has to know a write happened at all.
   */
  onChanged?: () => void;
}

/** Branch directory — CRUD plus the guest Wi-Fi settings (§19). */
export default function BranchesPanel({ onChanged }: Props = {}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const inScope = useRequireScope();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);
  const params = useMemo(
    () => pruneParams({ limit: 200, search: debouncedSearch }),
    [debouncedSearch],
  );

  const branchesQuery = useApi<unknown>('/branches', params);
  const branches = rowsOf<Branch>(branchesQuery.data).rows;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Branch | null>(null);
  const [deleting, setDeleting] = useState<Branch | null>(null);

  const canManage = can('settings.update');
  /**
   * `PATCH /branches/:id` and `DELETE /branches/:id` both declare
   * `@RequireScope(BRANCH, 'id')`, while `GET /branches` does not — so this list
   * shows a branch-scoped manager every branch in the tenant, and `settings.update`
   * alone says nothing about which of them they may touch. Without the mirror,
   * Edit and Delete look live on all of them and answer `Out of scope for BRANCH`
   * on click.
   */
  const canManageBranch = (branch: Branch) => canManage && inScope('BRANCH', branch.id);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  /**
   * Why `New branch` would be refused, or null when it would be taken. Same
   * shape as `StationsTab`'s `saveBlockedBy`: a disabled control that says
   * nothing is a dead end, and this is the first screen the owner of a newly
   * registered venue reaches — with zero branches, the empty row IS the screen.
   * `POST /branches` carries `settings.update` and no `@RequireScope`, so
   * permission is the whole precondition.
   */
  const createBlockedBy = canManage ? null : t('settings.branches.createDenied');

  const remove = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/branches/${deleting.id}`);
      toast.success(t('settings.branches.deleted'));
      branchesQuery.reload();
      onChanged?.();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[14rem] flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('settings.branches.searchPlaceholder')}
            aria-label={t('common.search')}
          />
        </div>
        {canManage && (
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            {t('settings.branches.create')}
          </Button>
        )}
      </div>

      {branchesQuery.loading ? (
        <LoadingState label={t('common.loading')} />
      ) : branchesQuery.error ? (
        <ErrorState message={branchesQuery.error} onRetry={branchesQuery.reload} />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('settings.branches.name')}</TH>
                <TH>{t('settings.branches.code')}</TH>
                <TH>{t('settings.branches.localeColumn')}</TH>
                <TH>{t('settings.branches.floors')}</TH>
                <TH>{t('settings.branches.wifi')}</TH>
                <TH>{t('settings.common.status')}</TH>
                <TH className="text-end">{t('settings.common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {branches.length === 0 ? (
                <TableEmpty colSpan={7}>
                  {/* The create button lives in the toolbar, which is exactly
                      where a first-time owner is not looking — they are looking
                      at the hole where their branches should be. So the empty
                      row carries the action too, and says why when it cannot. */}
                  <EmptyState
                    title={t('settings.branches.empty')}
                    hint={
                      createBlockedBy ??
                      (debouncedSearch
                        ? t('settings.branches.emptySearchHint')
                        : t('settings.branches.emptyHint'))
                    }
                    action={
                      canManage && !debouncedSearch ? (
                        <Button onClick={openCreate}>
                          <Plus className="h-4 w-4" />
                          {t('settings.branches.create')}
                        </Button>
                      ) : undefined
                    }
                  />
                </TableEmpty>
              ) : (
                branches.map((branch) => (
                  <TR key={branch.id}>
                    <TD>
                      <span className="font-medium">{branch.name}</span>
                      {branch.address && (
                        <p className="text-xs text-ink-muted">{branch.address}</p>
                      )}
                    </TD>
                    <TD>
                      <code className="text-xs" dir="ltr">
                        {branch.code}
                      </code>
                    </TD>
                    <TD className="text-ink-muted">
                      <span dir="ltr">
                        {branch.timezone} · {branch.currency}
                      </span>
                    </TD>
                    <TD>{branch._count?.floors ?? 0}</TD>
                    <TD>
                      {branch.wifiSsid ? (
                        <span className="flex items-center gap-1.5">
                          <Wifi className="h-4 w-4 text-green-600" />
                          <span dir="ltr">{branch.wifiSsid}</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-ink-muted">
                          <WifiOff className="h-4 w-4" />
                          {t('settings.branches.noWifi')}
                        </span>
                      )}
                    </TD>
                    <TD>
                      <Badge tone={branch.isActive ? 'success' : 'danger'}>
                        {branch.isActive
                          ? t('settings.common.active')
                          : t('settings.common.inactive')}
                      </Badge>
                    </TD>
                    <TD>
                      <div className="flex justify-end gap-1">
                        {canManage && (
                          <ScopeLock
                            locked={!canManageBranch(branch)}
                            reason={t('settings.branches.outOfScope')}
                          >
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!canManageBranch(branch)}
                              title={canManageBranch(branch) ? t('common.edit') : undefined}
                              aria-label={t('common.edit')}
                              onClick={() => {
                                setEditing(branch);
                                setFormOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </ScopeLock>
                        )}
                        {canManage && (
                          <ScopeLock
                            locked={!canManageBranch(branch)}
                            reason={t('settings.branches.outOfScope')}
                          >
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!canManageBranch(branch)}
                              title={canManageBranch(branch) ? t('common.delete') : undefined}
                              aria-label={t('common.delete')}
                              onClick={() => setDeleting(branch)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                            </Button>
                          </ScopeLock>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))
              )}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <BranchFormDialog
        open={formOpen}
        branch={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          branchesQuery.reload();
          onChanged?.();
        }}
      />

      <ConfirmDialog
        open={deleting !== null}
        title={t('settings.branches.deleteTitle')}
        message={t('settings.branches.deleteConfirm', { name: deleting?.name ?? '' })}
        confirmLabel={t('common.delete')}
        onConfirm={remove}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
